// B05 slice 7: the console's session — the TOKEN, never the password.
//
// Module-scope subscribe/notify singleton — the same shape socket.js and
// db-store.js already use, so it outlives any component and survives route
// changes without a provider.
//
// ── WHAT IS PERSISTED, AND WHY THAT IS SAFE ───────────────────────────────────
// Slice 7 held the session in memory only, because the WebSocket could then log
// in ONLY with `Authenticate{username,password}`: the store had to keep the
// PASSWORD for socket.js's reconnects (1s→30s backoff, each from `access =
// None`), and a password must never be persisted — so nothing was, and every
// reload re-prompted.
//
// `Authenticate` now carries the TRAILING optional `token` slice 4 reserved
// (`kinedb-protocol/src/message.rs`), and the server binds a WS connection to
// the session that token names WITHOUT the connection owning it (a socket close
// no longer revokes it — `handle_token_authenticate`). So the browser does
// `POST /login` ONCE, keeps only `{token, expires_at_ms, username}`, and
// re-presents the token on every socket and every `POST /sql`. The password
// dies with the login form's submit.
//
// The token is what a bearer is for: opaque random bytes, node-local and
// in-memory server-side (a restart / data reset invalidates it → one login),
// 24 h `SESSION_LIFETIME_MS`, revoked by `POST /logout` (the button),
// `ALTER USER … REVOKE SESSIONS` (the operator hammer), or expiry. It lives in
// `localStorage` (`kinedb.session`) so a reload, a new tab and a reopened
// browser all stay logged in while the session lives. The residual risk is the
// standard SPA one — an XSS in this bundle can read the token for the rest of
// its lifetime — which is bounded by the TTL and the revocation paths above,
// and is categorically smaller than the password it replaces.
//
// A dead persisted token cannot linger: it is dropped at load when the clock
// says so, and every server refusal (`onUnauthorized`, a WS `SESSION_REJECTED`)
// wipes it, so the gate reappears exactly once.
//
// B1237: the STORAGE rules above — the key, the well-formed shape, the clock
// check and the "drop AND remove a bad entry" rule — now live in
// `./session-token.js`, which imports nothing. The four demo pages read that
// module to reuse this session, and they must not import THIS file: the module
// body below pulls `@kinedb/client` in and installs its auth hooks. One copy of
// the rules, read by both sides, is the only way the two cannot drift.

import { defaultBaseUrl, setAuthHooks } from '@kinedb/client';
import { readLiveSession, writeSession } from './session-token.js';

/** @type {{token: string, expires_at_ms: number, username: string}|null} */
let _session = restore();
// B356-era follow-up (owner 2026-08-09): the server may be PERMISSIVE — the
// collector on :4900 always is (scripts/collector.sh pins both planes) — and a
// permissive server serves anonymous /sql and headerless WS. Forcing the login
// form there asks for a password the deployment deliberately does not require.
// `probeAnonymous` asks the server ONCE per app load; a later 401 (server
// re-pinned to enforcing, node restart) clears it via `onUnauthorized` and the
// gate returns. Held in memory only, same as the session.
let _anonymousOk = false;
const _listeners = new Set();

function notify() {
  for (const cb of _listeners) {
    try { cb(_session); } catch { /* listener error */ }
  }
}

/** Subscribe to login/logout. Returns an unsubscribe function. */
export function subscribe(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// Client-side expiry, from the `expires_at_ms` the mint handed us.
//
// Slice 7 wrote this as the ONLY available expiry signal, because `POST /sql`
// could not 401: a present-but-invalid bearer was counted
// (`auth.bearer_rejected_total`), warned about once, and then the request was
// served UNAUTHENTICATED — succeeding with MORE access than before — so a
// "401 → re-login" interceptor would have been dead code.
//
// B05 slice 9 made the 401 real, and `onUnauthorized` below is that interceptor.
// This check stays, and is no longer belt-and-braces either: it is the only signal
// that costs no round trip, and it stops a tab that has been closed for a day from
// presenting a token it can already prove is dead. The two are complementary —
// this one catches the clock, the interceptor catches everything the clock cannot
// see (a node restart empties its in-memory sessions; a load balancer moving the
// tab to a different node invalidates the token outright, since sessions are
// node-local and nothing is signed).
function live() {
  if (!_session) return null;
  if (Date.now() >= _session.expires_at_ms) {
    _session = null;
    persist(null);
    return null;
  }
  return _session;
}

/** The bearer for `POST /sql`, or `null`. */
export function getToken() {
  return live()?.token ?? null;
}

/** The logged-in username, for the chrome. */
export function getUsername() {
  return live()?.username ?? null;
}

/** `{username, token}` for the WS `Authenticate{token}` frame, or `null`. */
export function getCredentials() {
  const s = live();
  return s ? { username: s.username, token: s.token } : null;
}

// ── persistence ──────────────────────────────────────────────────────────────
// Only the three fields above, only in localStorage, and only ever the token —
// `setSession` takes (and ignores) whatever else a caller passes. The rules
// themselves are `./session-token.js`'s, so a demo page reads the same ones
// (B1237). The storage argument is left out on purpose: the console always wants
// `globalThis.localStorage`, and that is that module's default.
function persist(session) {
  writeSession(session);
}

// Read the persisted session at load. Anything not a live, well-formed
// `{token, expires_at_ms, username}` is dropped AND removed, so a bad entry
// cannot survive to be re-read next time.
function restore() {
  return readLiveSession();
}

/** Is there a usable session? (A real login — anonymous mode is separate.) */
export function isAuthenticated() {
  return live() !== null;
}

/** Did the server prove it serves anonymously? (Permissive plane.) */
export function isAnonymous() {
  return _anonymousOk;
}

/** The login gate's whole question: session OR proven-anonymous server. */
export function hasAccess() {
  return live() !== null || _anonymousOk;
}

/**
 * Ask the server whether anonymous access works: one tokenless `POST /sql`
 * with a no-op statement. 200 ⇒ the client plane is permissive (the collector
 * always is; a dev node may be) ⇒ the login gate is skipped. 401 or any
 * failure ⇒ gated as before — this can only ever REMOVE a prompt the server
 * itself does not require, never bypass one it does: every later request still
 * carries no token, so an enforcing server 401s and `onUnauthorized` restores
 * the gate. `deps` for tests, same shape as `login`.
 */
export async function probeAnonymous(deps = {}) {
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch;
  const baseUrl = deps.baseUrl ?? defaultBaseUrl();
  const url = (baseUrl ? baseUrl.replace(/\/+$/, '') : '') + '/sql';
  try {
    const r = await fetchImpl(url, { method: 'POST', body: 'SHOW TABLES' });
    if (r.ok) {
      _anonymousOk = true;
      notify();
      return true;
    }
  } catch { /* unreachable server: keep the gate */ }
  return false;
}

/** Install a session (also used by tests; `login` is the normal door). */
export function setSession({ token, expires_at_ms, username }) {
  _session = { token, expires_at_ms, username };
  persist(_session);
  notify();
}

/** Drop the session, in memory and on disk. */
export function clearSession() {
  _session = null;
  persist(null);
  notify();
}

/**
 * The logout button: revoke the bearer server-side (`POST /logout`), THEN
 * clear. The clear happens whatever the POST does — an unreachable server must
 * not pin the user in a session they asked to leave; the token then dies by
 * TTL / restart like any other. `deps` for tests, same shape as `login`.
 */
export async function logout(deps = {}) {
  const token = getToken();
  if (token) {
    const fetchImpl = deps.fetchImpl ?? globalThis.fetch;
    const baseUrl = deps.baseUrl ?? defaultBaseUrl();
    const url = (baseUrl ? baseUrl.replace(/\/+$/, '') : '') + '/logout';
    try {
      await fetchImpl(url, { method: 'POST', headers: { authorization: `Bearer ${token}` } });
    } catch { /* offline: forget it locally regardless */ }
  }
  clearSession();
}

/**
 * B05 slice 9: the server refused a request for lack of a valid credential.
 *
 * Called from `kinedb.js`'s single HTTP transport on any 401. Clearing the session
 * notifies every subscriber, which is what drops the app back to the login gate —
 * the same path a `logout` takes, so there is no second code path to keep correct.
 *
 * IDEMPOTENT on purpose: several in-flight requests can 401 together (the metrics
 * pollers overlap), and this must not fire a notify storm at subscribers for what
 * is one logical event. With no session there is nothing to clear and nothing to
 * announce.
 *
 * Deliberately NOT an automatic re-login: the store holds no password, and a
 * 401 can mean the account was revoked. Ask the human. The persisted copy goes
 * too, so a reload cannot resurrect a token the server has just refused.
 *
 * B1338: `refusedToken` is the bearer the refused request sent (`null` for none),
 * as `@kinedb/client` passes it. The pollers keep running behind the login form,
 * so a poll sent with the stale token, or with none, can 401 AFTER the login. That
 * refusal is about a credential this tab no longer holds: it is ignored. A call
 * with no argument keeps the old meaning, "the current credential was refused".
 */
export function onUnauthorized(refusedToken) {
  if (refusedToken !== undefined && refusedToken !== getToken()) return;
  if (_session === null && !_anonymousOk) return;
  _session = null;
  persist(null);
  // The server just refused an anonymous/invalid request, so proven-anonymous
  // no longer holds either (a permissive server 401s only a dead bearer, since
  // B998) — drop both so the gate re-probes/re-prompts instead of looping on a
  // dead assumption.
  _anonymousOk = false;
  notify();
}

/**
 * `POST /login` and store the result.
 *
 * The 200 body is the ONE JSON the server returns that is NOT the protocol
 * `Response` envelope: `{"token","expires_at_ms"}`, with no `type` and no
 * `elapsed_us`. Piping it through the usual `unwrap()` would mis-handle it, so it
 * is parsed here.
 *
 * A failure is HTTP 401 with the protocol error envelope and ONE message
 * (`invalid username or password`) covering a wrong password, an unknown user and
 * a malformed body alike — there is deliberately nothing more specific to show.
 *
 * `deps` exists so the test drives it without a server.
 */
export async function login(username, password, deps = {}) {
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch;
  const baseUrl = deps.baseUrl ?? defaultBaseUrl();
  const url = (baseUrl ? baseUrl.replace(/\/+$/, '') : '') + '/login';

  const r = await fetchImpl(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!r.ok) {
    let message = `HTTP ${r.status}`;
    try {
      const body = await r.text();
      const j = JSON.parse(body);
      if (j?.message) message = j.message;
      else if (body) message = body.slice(0, 240);
    } catch { /* keep the status */ }
    throw new Error(message);
  }

  const body = await r.json();
  if (!body?.token) throw new Error('login reply carried no token');
  setSession({
    token: body.token,
    // Belt for a server that ever omits it: an absent expiry must not read as
    // "expired at epoch 0" (instant logout) nor as "never expires".
    expires_at_ms: Number(body.expires_at_ms) || Date.now() + 60 * 60 * 1000,
    username,
  });
  return body;
}

/** Test-only: drop the singleton's state without notifying stale listeners. */
export function __resetForTests() {
  _session = null;
  _anonymousOk = false;
  _listeners.clear();
}

/** Test-only: what module load does — re-read the persisted session. */
export function __reloadForTests() {
  _session = restore();
}

// ── wire this store into @kinedb/client (B836) ───────────────────────
//
// The client is a package now; it does not import this file. This module body
// is the install point, and it runs whenever anything imports the store —
// `socket.js` does, and `App.jsx` imports `socket.js`, so the console always
// arrives here before the first request. One install covers every
// `new KineDB(...)` in the app, exactly as the old hard import did.
//
// Ordering is safe: this file imports `kinedb.js`, so the client's module body
// has already run, and the client READS the hooks per request instead of
// resolving them at construction.
setAuthHooks({ getToken, onUnauthorized });
